import type { ReactNode } from "react";
import { Link, type To } from "react-router";
import {
  ArrowRight,
  Clock,
  CreditCard,
  FileCheck,
  FileText,
  Mail,
  Package,
  Phone,
  PoundSterling,
  ScanSearch,
  ShoppingBag,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type {
  CustomerHome,
  CustomerHomeAttentionItem,
  ResolvedThemeValues,
} from "@shared-core/data/customer-api-types";
import { isCisOrderNo } from "@shared-core/domain/cis-order-no";
import {
  formatCurrencyGbp,
  formatDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../../app/components/primitives/button";
import { cn } from "../../../app/components/primitives/utils";
import { orderLinkTarget, orderTitle } from "../PortalOrderRow";
import { WelcomeText } from "../theme/WelcomeText";

export interface PortalHomeShortcut {
  label: string;
  to: string;
  icon: LucideIcon;
}

export const PORTAL_HOME_SHORTCUT_ICONS = {
  catalogue: ShoppingBag,
  orders: Package,
  artwork: ScanSearch,
  spend: PoundSterling,
} satisfies Record<string, LucideIcon>;

/** What the home page has read of the customer's orders. */
export type PortalHomeData =
  | { status: "loading" }
  | { status: "error"; onRetry?: () => void }
  | { status: "ready"; home: CustomerHome };

const ATTENTION_ICONS: Record<CustomerHomeAttentionItem["kind"], LucideIcon> = {
  payment_due: CreditCard,
  po_number: FileText,
  files_owed: Upload,
  new_file_needed: Upload,
  proof_to_approve: FileCheck,
  awaiting_customer: Clock,
};

/** The portal's own page for an attention item's order: a CIS order's page by its number, a Tharstern one by its ref. */
function attentionLinkTarget(item: CustomerHomeAttentionItem): string {
  return isCisOrderNo(item.order_ref)
    ? paths.portalCisOrder({ orderNo: item.order_ref })
    : paths.portalOrderDetail({ orderId: item.order_ref });
}

function attentionDetail(item: CustomerHomeAttentionItem): string {
  return [
    item.description,
    item.order_no,
    item.amount_pence !== null
      ? formatCurrencyGbp(item.amount_pence / 100, 2)
      : null,
    item.due_at ? `due ${formatDateOrDash(item.due_at)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** A link in the real portal; plain text in the staff preview, which must not navigate away. */
function MaybeLink({
  to,
  linkTo,
  className,
  children,
}: {
  to: string;
  linkTo: ((path: string) => To) | null;
  className?: string;
  children: ReactNode;
}) {
  return linkTo ? (
    <Link to={linkTo(to)} className={className}>
      {children}
    </Link>
  ) : (
    <span className={className}>{children}</span>
  );
}

const SHORTCUT_ALIGN = {
  left: "justify-start text-left items-start",
  centre: "justify-center text-center items-center",
  right: "justify-end text-right items-end",
} as const;

function Banner({ theme }: { theme: ResolvedThemeValues }) {
  const capabilities = usePlatformCapabilities();
  if (!theme.banner_image) return null;
  if (theme.banner_is_video) {
    // Muted and looping like BrandHub's; it never starts itself for someone who asked for reduced motion.
    const reducedMotion = capabilities.matchesMediaQuery(
      "(prefers-reduced-motion: reduce)",
    );
    return (
      <video
        src={theme.banner_image}
        className="absolute inset-0 size-full object-cover"
        muted
        loop
        playsInline
        autoPlay={!reducedMotion}
        aria-hidden
        data-testid="portal-home-banner-video"
      />
    );
  }
  return (
    <img
      src={theme.banner_image}
      alt=""
      className="absolute inset-0 size-full object-cover"
    />
  );
}

/**
 * The portal's branded home page (docs/features/portal-theme, layout B): the banner carries the welcome and the
 * shortcuts, positioned by the theme; below it, what needs the person's attention, their recent orders and who to ask
 * for help. Presentational, so the staff editor previews exactly what the customer sees (with `linkTo` null, nothing
 * navigates).
 */
export function PortalHomeView({
  theme,
  shortcuts,
  data,
  linkTo,
}: {
  theme: ResolvedThemeValues;
  shortcuts: PortalHomeShortcut[];
  data: PortalHomeData;
  /** The portal's address for a page; null in the staff preview, where links are switched off. */
  linkTo: ((path: string) => To) | null;
}) {
  const align = SHORTCUT_ALIGN[theme.shortcut_position];
  return (
    <div className="space-y-6">
      <section
        className="relative overflow-hidden rounded-xl bg-primary"
        aria-labelledby="portal-home-welcome"
      >
        <Banner theme={theme} />
        {theme.banner_overlay && (
          <div
            className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/30 to-black/5"
            aria-hidden
          />
        )}
        <div
          className={cn(
            "relative flex min-h-56 flex-col gap-5 px-6 py-8 sm:px-10 sm:py-10",
            align,
          )}
          style={{ color: "var(--portal-banner-text)" }}
        >
          <div className="max-w-xl space-y-2">
            <h1
              id="portal-home-welcome"
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {theme.welcome_title}
            </h1>
            <WelcomeText
              text={theme.welcome_text}
              className="space-y-2 text-sm leading-relaxed opacity-95 sm:text-base"
            />
          </div>
          <nav
            aria-label="Shortcuts"
            className={cn("flex w-full flex-wrap gap-3", align)}
          >
            {shortcuts.map(({ label, to, icon: Icon }) => (
              <MaybeLink
                key={label}
                to={to}
                linkTo={linkTo}
                className="flex min-w-36 items-center gap-2 rounded-lg bg-white/95 px-4 py-3 text-sm font-medium text-gray-900 shadow-sm transition hover:bg-white"
              >
                <Icon className="size-4 text-primary" aria-hidden />
                {label}
              </MaybeLink>
            ))}
          </nav>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <section
          className="rounded-xl border bg-card p-5"
          aria-labelledby="portal-home-attention"
        >
          <h2 id="portal-home-attention" className="text-base font-semibold">
            Needs your attention
          </h2>
          <HomeOrders data={data} linkTo={linkTo} />
        </section>

        <aside
          className="h-fit rounded-xl border bg-card p-5"
          aria-labelledby="portal-home-help"
        >
          <h2 id="portal-home-help" className="text-base font-semibold">
            Need help?
          </h2>
          {theme.support_name && (
            <p className="mt-2 text-sm">{theme.support_name}</p>
          )}
          {theme.support_email && (
            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="size-4 shrink-0" aria-hidden />
              <a
                href={`mailto:${theme.support_email}`}
                className="break-all hover:underline"
              >
                {theme.support_email}
              </a>
            </p>
          )}
          {theme.support_phone && (
            <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="size-4 shrink-0" aria-hidden />
              {theme.support_phone}
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}

function HomeOrders({
  data,
  linkTo,
}: {
  data: PortalHomeData;
  linkTo: ((path: string) => To) | null;
}) {
  if (data.status === "loading") {
    return (
      <p className="mt-3 text-sm text-muted-foreground" role="status">
        Loading your orders…
      </p>
    );
  }
  if (data.status === "error") {
    return (
      <div className="mt-3 space-y-2">
        <p className="text-sm text-muted-foreground">
          We couldn&apos;t load your orders just now.
        </p>
        {data.onRetry && (
          <Button variant="outline" size="sm" onClick={data.onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }
  const { home } = data;
  const more = home.attention_total - home.attention.length;
  return (
    <>
      {home.attention.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Nothing is waiting on you.
        </p>
      ) : (
        <ul className="mt-3 divide-y">
          {home.attention.map((item) => {
            const Icon = ATTENTION_ICONS[item.kind];
            return (
              <li key={`${item.kind} ${item.order_ref}`}>
                <MaybeLink
                  to={attentionLinkTarget(item)}
                  linkTo={linkTo}
                  className="flex items-center gap-3 py-3"
                >
                  <Icon className="size-4 shrink-0 text-primary" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">
                      {item.sentence}
                    </span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {attentionDetail(item)}
                    </span>
                  </span>
                  <ArrowRight
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                </MaybeLink>
              </li>
            );
          })}
        </ul>
      )}
      {more > 0 && (
        <MaybeLink
          to={paths.portalOrders()}
          linkTo={linkTo}
          className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary"
        >
          {home.is_attention_total_at_least
            ? `and at least ${more} more`
            : `and ${more} more`}
          <ArrowRight className="size-4" aria-hidden />
        </MaybeLink>
      )}

      <h2 className="mt-6 text-base font-semibold">Recent orders</h2>
      {home.recent_orders.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No orders yet.</p>
      ) : (
        <ul className="mt-3 divide-y">
          {home.recent_orders.map((order) => {
            const target = orderLinkTarget(order);
            const content = (
              <>
                <span className="min-w-0">
                  <span className="font-medium">{orderTitle(order)}</span>
                  <span className="ml-2 text-muted-foreground">
                    {[
                      order.order_no ?? order.order_ref,
                      order.ordered_at
                        ? `placed ${formatDateOrDash(order.ordered_at)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="text-muted-foreground">
                  {order.status_line}
                </span>
              </>
            );
            const rowClass =
              "flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3 text-sm";
            return (
              <li key={order.order_no ?? order.order_ref ?? orderTitle(order)}>
                {target ? (
                  <MaybeLink to={target} linkTo={linkTo} className={rowClass}>
                    {content}
                  </MaybeLink>
                ) : (
                  <div className={rowClass}>{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <MaybeLink
        to={paths.portalOrders()}
        linkTo={linkTo}
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary"
      >
        All orders <ArrowRight className="size-4" aria-hidden />
      </MaybeLink>
    </>
  );
}
